/**
 * Batching primitives shared by the Firestore write path and the bulk edit engine.
 *
 * Kept free of any Firebase or store imports so the planning and batching logic can be
 * reasoned about, and tested, on its own.
 */

/** Firestore refuses a batch larger than this, so it is both the default and the ceiling. */
export const MAX_BATCH_SIZE = 500;

/** How many batch commits are kept in flight. Shared so estimates match the real run. */
export const DEFAULT_BATCH_CONCURRENCY = 3;

/** A single record write for a batched run. Setting `remove` deletes the record. */
export interface BatchRecordWrite {
	id: string;
	values?: { [key: string]: any };
	remove?: boolean;
}

export interface BatchWriteFailure {
	id: string;
	error: string;
}

export interface BatchWriteResult {
	succeeded: string[];
	failed: BatchWriteFailure[];
}

export interface BatchWriteOptions {
	chunkSize?: number;
	concurrency?: number;
	onProgress?: (completed: number) => void;
	isCancelled?: () => boolean;
	/** Records in a batch that is about to be committed. */
	onChunkStart?: (ids: string[]) => void;
	/** Records in a batch that is being retried after a busy response. */
	onChunkRetry?: (ids: string[], attempt: number) => void;
	/** Outcome for the records in a batch, once settled. */
	onChunkSettled?: (succeeded: string[], failed: BatchWriteFailure[]) => void;
}

export function chunkWrites<T>(items: T[], size: number): T[][] {
	let chunks: T[][] = [];

	for (let i = 0; i < items.length; i += size) {
		chunks.push(items.slice(i, i + size));
	}

	return chunks;
}

/**
 * Firestore error codes that mean "busy, try again" rather than "this write is wrong".
 *
 * Splitting a throttled batch into single writes would multiply the request count and make
 * the overload worse, so these are retried as a whole batch instead.
 */
const RETRYABLE_CODES = ['resource-exhausted', 'unavailable', 'aborted', 'deadline-exceeded', 'internal', 'cancelled'];

export function isRetryableWriteError(error: any): boolean {
	let code = String(error?.code ?? '').toLowerCase();

	return RETRYABLE_CODES.some((candidate) => code === candidate || code.endsWith(`/${candidate}`));
}

export interface CommitRetryOptions {
	maxAttempts?: number;
	baseDelayMs?: number;
	isCancelled?: () => boolean;
	onRetry?: (attempt: number) => void;
	/** Injected in tests so the backoff does not actually wait. */
	wait?: (ms: number) => Promise<void>;
}

const defaultWait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * Commits with exponential backoff, so Firestore's ramp up limits are respected rather than
 * fought. `commit` is a factory because a committed batch cannot be reused.
 */
export async function commitWithRetry(commit: () => Promise<void>, options: CommitRetryOptions = {}): Promise<void> {
	let maxAttempts = Math.max(options.maxAttempts ?? 5, 1);
	let baseDelayMs = options.baseDelayMs ?? 500;
	let wait = options.wait ?? defaultWait;
	let attempt = 0;

	for (;;) {
		try {
			await commit();
			return;
		} catch (error) {
			attempt++;

			if (attempt >= maxAttempts || !isRetryableWriteError(error) || options.isCancelled?.()) {
				throw error;
			}

			options.onRetry?.(attempt);

			// Jitter keeps parallel batches from retrying in lockstep.
			await wait(Math.round(baseDelayMs * Math.pow(2, attempt - 1) * (0.5 + Math.random())));
		}
	}
}
